import { BaseApiService } from '@/shared/api/services/base.service';

/**
 * The Facade Kit SKU planted on the purchase intent. Stays the canonical
 * `ai.getfacade.*` identifier (shared with the Apple/Google catalog) — the
 * server resolves the actual pack from the Paddle transaction's price_id, so
 * this is just the correlation row's product label and must satisfy the
 * intent endpoint's `ai.getfacade.` shape rule.
 */
export const FACADE_KIT_SKU = 'ai.getfacade.pack.facade';

/**
 * Canonical `ai.getfacade.*` product ids for the account-level entitlement
 * products. Like {@link FACADE_KIT_SKU}, these are just the correlation row's
 * label (must satisfy the intent endpoint's `ai.getfacade.` shape rule) — the
 * server resolves the actual product from the Paddle transaction's price_id.
 */
export const PASS_SKU_BY_ENTITLEMENT: Record<string, string> = {
  pro_pass: 'ai.getfacade.pass.pro',
  pro_plan: 'ai.getfacade.subscription.unlimited',
};

export interface PurchaseIntent {
  correlation_token: string;
  kind: string;
  product_id: string;
  project_id: string | null;
  status: string;
  expires_at: string;
}

/**
 * Purchase correlation (web). Mints a `purchase_intents` row BEFORE opening the
 * Paddle checkout; the returned `correlation_token` is planted in Paddle's
 * customData so the webhook recovers buyer + building deterministically.
 * Mirrors the Apple `appAccountToken` / Google `obfuscatedProfileId` flow.
 */
class PurchaseService extends BaseApiService {
  async createPackIntent(projectId: string): Promise<PurchaseIntent> {
    const body = this.formatJsonApiData('purchase-intent', {
      kind: 'pack',
      product_id: FACADE_KIT_SKU,
      provider: 'paddle',
      project_id: projectId,
    });

    const response = await this.post<{
      data: { attributes: PurchaseIntent };
    }>('/v1/purchases/intents', body);

    return response.data.attributes;
  }

  /**
   * Account-level purchase intent for a lifetime Pass (Tokens Pro Pass). No
   * `project_id` — passes are not building-bound (the intent endpoint PROHIBITS
   * a project for `pass` kind). The webhook grants the entitlement on
   * transaction.completed.
   */
  async createPassIntent(productSku: string): Promise<PurchaseIntent> {
    return this.createAccountIntent('pass', productSku);
  }

  /**
   * Account-level purchase intent for a renewable subscription (Pro Plan).
   * No `project_id`; the webhook grants the entitlement and drives the
   * subscription lifecycle from Paddle `subscription.*` events.
   */
  async createSubscriptionIntent(productSku: string): Promise<PurchaseIntent> {
    return this.createAccountIntent('subscription', productSku);
  }

  /**
   * Account-level purchase intent for a token package. No `project_id`;
   * the webhook resolves the package from the Paddle price_id and credits
   * the token ledger on transaction.completed.
   */
  async createTokenIntent(productSku: string): Promise<PurchaseIntent> {
    return this.createAccountIntent('token', productSku);
  }

  private async createAccountIntent(
    kind: 'pass' | 'subscription' | 'token',
    productSku: string,
  ): Promise<PurchaseIntent> {
    const body = this.formatJsonApiData('purchase-intent', {
      kind,
      product_id: productSku,
      provider: 'paddle',
    });

    const response = await this.post<{
      data: { attributes: PurchaseIntent };
    }>('/v1/purchases/intents', body);

    return response.data.attributes;
  }
}

export const purchaseService = new PurchaseService();
